// conventions-allow: file-size — There is no reason to split this file into smaller components since they are not reusable

import MonacoEditor from '@monaco-editor/react'
import { useOptimizationSnapshotDiff, useOptJobReview } from 'api/optimizations/optimizations.api'
import classNames from 'classnames'
import { t } from 'i18next'
import React, { ReactElement, useState } from 'react'
import Badge from 'ui/atoms/Badge/Badge'
import Card from 'ui/atoms/Card/Card'
import InfoBanner from 'ui/components/InfoBanner/InfoBanner'
import ModuleHeader from 'ui/components/ModuleHeader/ModuleHeader'
import MonacoStableDiffEditor from 'ui/components/MonacoStableDiffEditor/MonacoStableDiffEditor'
import View from 'ui/components/View/View'
import UiConfig from 'ui/uiConfig/UiConfig'
import Datetime from 'utils/datetime/datetime'
import OptimizationsDebugView from '../OptimizationsDebugView/OptimizationsDebugView'
import DownloadJSONButton from './DownloadJSONButton'
import {
  getMostRecentFinishedOptJobAndQueueItem,
  getOptJobRunPeriod,
  OptJobAndQueueItemWithType,
} from './Optimizations.helper'
import OptimizationsDropdown from './OptimizationsDropdown'
import OptimizationsTour from './OptimizationsTour'
import styles from './OptimizationsView.module.less'
import { useOptimizationsReviewJobs } from './utils/hooks/useOptimizationsReviewJobs'

const MEASUREMENT_LOOKBACK_HOURS = 168
export default function OptimizationsView(): ReactElement {
  const [runTour, setRunTour] = useState<null | number>(null)
  const [selectedOptJobAndQueueItem, setSelectedOptJobAndQueueItem] = useState<OptJobAndQueueItemWithType | null>(null)

  const { latestOptJobsAndQueueItems, isFetchingOptJobAndQueueItems } = useOptimizationsReviewJobs()

  const { data: dataForSelectedOptJob } = useOptJobReview({
    optJobId: selectedOptJobAndQueueItem?.opt_job_id,
    queueItemId: selectedOptJobAndQueueItem?.queue_item_id,
  })

  const previousFinishedOptJobAndQueueItem = getMostRecentFinishedOptJobAndQueueItem(
    selectedOptJobAndQueueItem,
    latestOptJobsAndQueueItems
  )
  const { data: settingsDiff } = useOptimizationSnapshotDiff({
    previousOptJobId: previousFinishedOptJobAndQueueItem?.opt_job_id,
    previousQueueItemId: previousFinishedOptJobAndQueueItem?.queue_item_id,
    nextOptJobId: selectedOptJobAndQueueItem?.opt_job_id,
    nextQueueItemId: selectedOptJobAndQueueItem?.queue_item_id,
  })
  //TODO:
  // - add start and end time for optimization (optjob), do we know if we did a 5 or 7 day optimization?
  // - add information about who started the optimization (optjob)

  const selectedOptJobCreatedAt = Datetime.toISOString(
    selectedOptJobAndQueueItem?.opt_job_created_at ?? Datetime.getISONow()
  )
  const selectedOptJobCreatedAtWholeHour = Datetime.ISODatetimeTo00Minutes(selectedOptJobCreatedAt)
  const selectedOptJobRunPeriod = getOptJobRunPeriod(selectedOptJobAndQueueItem?.opt_job_type, selectedOptJobCreatedAt)

  return (
    <>
      <View
        header={
          <ModuleHeader
            infoHeader={t('Review optimizations')}
            onInfoButtonClick={() => {
              // safely pick the first item if available
              setSelectedOptJobAndQueueItem(latestOptJobsAndQueueItems?.[0] ?? null)
              setRunTour(0)
            }}
          ></ModuleHeader>
        }
        stickyHeader
      >
        {/* Content: two cards per row max, responsive using flexbox */}
        <div className={classNames(styles.ViewInnerPadding, 'card_content')}>
          <OptimizationsDropdown
            onSelect={setSelectedOptJobAndQueueItem}
            latestOptJobsAndQueueItems={latestOptJobsAndQueueItems}
            isFetchingItems={isFetchingOptJobAndQueueItems}
          />
          <OptimizationsTour runTour={runTour} setRunTour={(state: boolean) => setRunTour(state ? 0 : null)} />
          <div className={styles.ContentColumns}>
            <div className={styles.MainColumn}>
              {selectedOptJobAndQueueItem && (
                <>
                  <div className={styles.CardsContainer}>
                    <div className={classNames(styles.CardItemFullWidth, 'solver_hint')}>
                      <Card
                        key={`${selectedOptJobAndQueueItem?.opt_job_id}_${dataForSelectedOptJob ? 'ready' : 'pending'}`}
                        title={t('Solver hint')}
                        titleSize="medium"
                        collapsible
                        defaultCollapsed={dataForSelectedOptJob?.infeasibility_hint == null}
                        titleEndAdornment={<Badge title={t('CLUE — NOT A DIAGNOSIS')} info />}
                      >
                        <InfoBanner
                          style={'info'}
                          text={t(
                            'The solver returned this line while proving the job unsolvable. It points at one place in the model that could not be satisfied, not a cause or a fix. Identifiers are internal model names and do not have to match any name shown in this view.'
                          )}
                        />

                        <div className="flex flex-col md:flex-row gap-4 mt-2">
                          <div className="w-full md:w-1/2 p-2 h-[560px] overflow-hidden">
                            <DownloadJSONButton
                              content={dataForSelectedOptJob?.infeasibility_hint}
                              filename={`${selectedOptJobAndQueueItem?.opt_job_created_at}_infeasibility_hint`}
                            />
                            <MonacoEditor
                              height="500px"
                              language="json"
                              value={JSON.stringify(dataForSelectedOptJob?.infeasibility_hint, null, 2)}
                              options={{
                                minimap: { enabled: false },
                                readOnly: true,
                                wordWrap: 'on',
                                automaticLayout: true,
                                formatOnType: false,
                                formatOnPaste: false,
                              }}
                            />
                          </div>
                          <div className="w-full md:w-1/2 bg-gray-100 rounded-md p-3">
                            <p className="text-xs uppercase font-semibold text-gray-500 mb-2">
                              {t('WHERE TO TAKE IT')}
                            </p>
                            <ul className="list-disc pl-4 space-y-1">
                              <li>
                                {t(
                                  'Unsolvable jobs often indicate issues with the settings, check the card called "{{changedSettings}}"',
                                  {
                                    changedSettings: t('Changed, added or removed settings'),
                                  }
                                )}
                              </li>
                              <li>
                                {t(
                                  'Check {{inputData}}, {{forecasts}} and {{measurementValues}} for the period {{runPeriod}} this job ran.',
                                  {
                                    inputData: t('Input data'),
                                    forecasts: t('Forecasts'),
                                    measurementValues: t('Measurement values'),
                                    runPeriod: `${Datetime.toLocalTime(selectedOptJobRunPeriod.start, 'longDayAndMonthTextWithoutYearIfCurrentYear')} – ${Datetime.toLocalTime(selectedOptJobRunPeriod.end, 'longDayAndMonthTextWithoutYearIfCurrentYear')}`,
                                  }
                                )}
                              </li>

                              <li>
                                {t(
                                  'Handing it over? Bring the raw line by {{downloadJson}}, the opt job id {{optJobId}} and the run period {{runPeriod}}.',
                                  {
                                    optJobId: selectedOptJobAndQueueItem?.opt_job_id ?? '—',
                                    runPeriod: `${Datetime.toLocalTime(selectedOptJobRunPeriod.start, 'longDayAndMonthTextWithoutYearIfCurrentYear')} – ${Datetime.toLocalTime(selectedOptJobRunPeriod.end, 'longDayAndMonthTextWithoutYearIfCurrentYear')}`,
                                    downloadJson: t('Download JSON'),
                                  }
                                )}
                              </li>
                            </ul>
                          </div>
                        </div>
                      </Card>
                    </div>
                    <div className={classNames(styles.CardItem, 'error_message')}>
                      <Card title={t('Error message')} titleSize="medium" collapsible>
                        <InfoBanner
                          style={'info'}
                          text={t('Some jobs have errors even if the optimization has finished without any problems.')}
                        />

                        <div className="p-2 w-full h-[560px] overflow-hidden">
                          <DownloadJSONButton
                            content={dataForSelectedOptJob?.errors}
                            filename={`${selectedOptJobAndQueueItem?.opt_job_created_at}_error_message`}
                          />
                          <MonacoEditor
                            height="500px"
                            language="json"
                            value={JSON.stringify(dataForSelectedOptJob?.errors, null, 2)}
                            options={{
                              minimap: { enabled: false },
                              readOnly: true,
                              wordWrap: 'on',
                              automaticLayout: true,
                              formatOnType: false,
                              formatOnPaste: false,
                            }}
                          />
                        </div>
                      </Card>
                    </div>

                    <div className={classNames(styles.CardItem, 'settings_comparison')}>
                      <Card title={t('Changed, added or removed settings')} titleSize="medium" collapsible>
                        <InfoBanner
                          style={'info'}
                          text={t(
                            'If there exists a previous finished optimization, the settings used (default value, deviation and base settings) in that optimization will be compared to the settings used in the selected optimization. The settings with the highest priority are the ones that are active, see priorities below.'
                          )}
                        />
                        <h3 style={{ display: 'flex' }}>
                          <b>{'Priority 0'}</b>
                          {': '}
                          {t(
                            'Default values, the values that are specified in the model applied if no other values are specified in other settings.'
                          )}
                        </h3>
                        <h3 style={{ display: 'flex' }}>
                          <b>{'Priority 10'}</b>
                          {': '}
                          {t(
                            'Base settings, the values from these settings are active if no deviation setting is specified.'
                          )}
                        </h3>
                        <h3 style={{ display: 'flex' }}>
                          <b>{'Priority 30'}</b>
                          {': '}
                          {t('Deviation settings, these are the settings that are active.')}
                        </h3>
                        <br></br>
                        <h2>
                          <b>
                            {`${t(`Compares the settings used in the selected optimization to the ones used in optimization with id:`)} ${previousFinishedOptJobAndQueueItem ? previousFinishedOptJobAndQueueItem.opt_job_id : t('(a previous finished optimization does not exist)')}`}
                          </b>
                        </h2>

                        {settingsDiff && (
                          <div className="p-2 w-full h-[560px] overflow-hidden">
                            <DownloadJSONButton
                              content={{
                                previous_optimization: settingsDiff.left_snapshot,
                                current_optimization: settingsDiff.right_snapshot,
                              }}
                              filename={`${selectedOptJobAndQueueItem?.opt_job_created_at}_digital_twin_settings`}
                            />
                            <MonacoStableDiffEditor
                              height="500px"
                              language="json"
                              original={JSON.stringify(settingsDiff.left_snapshot, null, 2)}
                              modified={JSON.stringify(settingsDiff.right_snapshot, null, 2)}
                              options={{
                                minimap: { enabled: false },
                                readOnly: true,
                                wordWrap: 'on',
                                automaticLayout: true,
                                formatOnType: false,
                                formatOnPaste: false,
                                renderSideBySide: false,
                              }}
                            />
                          </div>
                        )}
                      </Card>
                    </div>

                    <div className={classNames(styles.CardItemFullWidth, 'input_data')}>
                      <Card title={t('Input data')} titleSize="medium" collapsible defaultCollapsed>
                        <InfoBanner
                          style={'info'}
                          text={t(
                            'This is the input data that was used for this optimization job. The input data is based on the settings active at the time of optimization, demands and costs. If values are specified in an array i.e [], each value is for one hour of the optimization period.'
                          )}
                        />

                        <DownloadJSONButton
                          content={dataForSelectedOptJob?.input_data}
                          filename={`${selectedOptJobAndQueueItem?.opt_job_created_at}_optimization_input`}
                        />
                        <MonacoEditor
                          height="700px"
                          language="json"
                          value={JSON.stringify(dataForSelectedOptJob?.input_data, null, 2)}
                          options={{
                            minimap: { enabled: false },
                            readOnly: true,
                            wordWrap: `on`,
                            automaticLayout: true,
                            formatOnType: false,
                            formatOnPaste: false,
                          }}
                        />
                      </Card>
                    </div>

                    <div className={classNames(styles.CardItemFullWidth, 'forecasts')}>
                      <Card
                        title={`${t('Forecasts')} (${t('as they were on')} ${Datetime.toLocalTime(selectedOptJobCreatedAtWholeHour, 'longDayAndMonthTextWithoutYearIfCurrentYear')})`}
                        titleSize="medium"
                        collapsible
                        defaultCollapsed
                      >
                        <InfoBanner
                          style={'info'}
                          text={t(
                            'These are the forecasts and what they looked like on the time of the optimization, on hour {{timeForOptimization}} (i.e optimization job start).',
                            {
                              timeForOptimization: Datetime.toLocalTime(selectedOptJobCreatedAtWholeHour, 'longDay'),
                            }
                          )}
                        />
                        <UiConfig
                          type="optimizations_info_forecast_diff"
                          datasetStartTime={selectedOptJobRunPeriod.start}
                          datasetEndTime={selectedOptJobRunPeriod.end}
                          overrideAlias={{
                            selected_opt_job_created_at: selectedOptJobCreatedAtWholeHour ?? '',
                            previous_opt_job_created_at: previousFinishedOptJobAndQueueItem?.opt_job_created_at ?? '',
                          }}
                        />
                      </Card>
                    </div>
                    <div className={classNames(styles.CardItemFullWidth, 'measurement_values')}>
                      <Card
                        title={`${t('Measurement values')} (${t('as they were on')} ${Datetime.toLocalTime(selectedOptJobCreatedAtWholeHour, 'longDayAndMonthTextWithoutYearIfCurrentYear')})`}
                        titleSize="medium"
                        collapsible
                        defaultCollapsed
                      >
                        <InfoBanner
                          style={'info'}
                          text={t(
                            'This graph shows the measurement values for the optimization job that were available on the time of the optimization and 168 hours (1 week) before the time of the optimization.'
                          )}
                        />
                        <UiConfig
                          type="optimizations_info_outcome"
                          datasetStartTime={Datetime.subtractHours(
                            selectedOptJobCreatedAtWholeHour,
                            MEASUREMENT_LOOKBACK_HOURS
                          )}
                          datasetEndTime={selectedOptJobCreatedAtWholeHour}
                          overrideAlias={{
                            selected_opt_job_created_at: selectedOptJobCreatedAtWholeHour ?? '',
                            previous_opt_job_created_at: previousFinishedOptJobAndQueueItem?.opt_job_created_at ?? '',
                          }}
                        />
                      </Card>
                    </div>
                  </div>
                </>
              )}
            </div>
            {selectedOptJobAndQueueItem && (
              <div className={classNames(styles.SideColumn, 'debug_log')}>
                <Card title={t('Debug log')} collapsible titleSize="medium">
                  <OptimizationsDebugView
                    optJobId={selectedOptJobAndQueueItem.opt_job_id}
                    queueItemId={selectedOptJobAndQueueItem.queue_item_id}
                  />
                </Card>
              </div>
            )}
          </div>
        </div>
      </View>
    </>
  )
}
